import { DataGrid, GridColumnsConfigProvider } from '@finbeat/react-toolkit';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import useAuth from 'hooks/useAuth';
import { ClientPaymentInfo, PaymentCovers } from 'models/api/payment';
import DataGridProp from 'models/dataGridProp';
import { RefObject, useEffect, useMemo, useState } from 'react';
import { RowsChangeData } from 'react-data-grid';
import { Trans } from 'react-i18next';
import { useQuery } from 'react-query';
import '../OrderInfo.scss';
import AgreementProlongations from '../Tables/AgreementProlongations';
import PaymentCoversTable from '../Tables/PaymentCoversTable';

type MasterRow = ClientPaymentInfo & { type: 'MASTER' } & { expanded: boolean };

type DetailRow = { covers: PaymentCovers[] } & { type: 'DETAIL' };

type ClientPaymentInfoTable = DataGridProp & (MasterRow | DetailRow);

const PaymentsTab = ({
    paymentsQueryFn,
    isOpen,
    container,
    orderId,
}: {
    paymentsQueryFn: () => Promise<ClientPaymentInfo[]>;
    isOpen: boolean;
    container?: RefObject<HTMLElement>;
    orderId?: number;
}) => {
    const className = 'payments-info';
    const { hasRole } = useAuth();
    const [tableInfo, setTableInfo] = useState<ClientPaymentInfoTable[] | null>(null);
    const [wasOpened, setWasOpened] = useState(false);

    useEffect(() => {
        !wasOpened && setWasOpened(isOpen);
    }, [isOpen]);

    const paymentsQuery = useQuery(['payments', orderId], () => paymentsQueryFn(), {
        onSuccess: (data) =>
            setTableInfo(data.map((x) => ({ ...x, type: 'MASTER', expanded: false }))),
        enabled: wasOpened && !tableInfo,
    });

    const onRowsChange = (
        rows: ClientPaymentInfoTable[],
        { indexes }: RowsChangeData<ClientPaymentInfoTable>
    ) => {
        const row = rows[indexes[0]];
        if (row.type === 'MASTER') {
            if (!row.expanded) {
                rows.splice(indexes[0] + 1, 1);
            } else {
                rows.splice(indexes[0] + 1, 0, {
                    type: 'DETAIL',
                    covers: row.covers,
                });
            }
            setTableInfo(rows);
        }
    };

    const blockSize = useMemo(
        () =>
            tableInfo
                ? tableInfo.reduce(
                      (sum, val) =>
                          (sum += val.type === 'DETAIL' ? 90 + val.covers.length * 45 : 45),
                      45
                  )
                : 90,
        [tableInfo]
    );

    return (
        <>
            {hasRole('orderGetAgreementProlongationsInfo') && (
                <AgreementProlongations container={container} wasOpened={wasOpened} />
            )}
            <GridColumnsConfigProvider<ClientPaymentInfoTable>
                storagePath="payments-grid"
                initColumns={[
                    {
                        key: 'paymentId',
                        name: '#',
                        colSpan: (args) => {
                            if (args.type === 'ROW' && args.row.type === 'DETAIL') {
                                return 7;
                            }
                        },
                        cellClass(row) {
                            return row.type === 'DETAIL' ? `${className}__detail` : undefined;
                        },
                        formatter({ row, onRowChange }) {
                            if (row.type === 'DETAIL') {
                                return <PaymentCoversTable covers={row.covers} />;
                            }

                            return row.covers.length ? (
                                <a
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onRowChange({ ...row, expanded: !row.expanded });
                                    }}
                                    href="#"
                                    className="toggle-covers"
                                >
                                    {row.paymentId}
                                </a>
                            ) : (
                                <>{row.paymentId}</>
                            );
                        },
                    },
                    {
                        key: 'loanAccountNo',
                        name: <Trans i18nKey="BackOfficePersonalAccount" />,
                    },
                    {
                        key: 'amount',
                        name: <Trans i18nKey="BackOfficeAmount" />,
                        formatter: ({ row }) =>
                            row.type === 'MASTER' ? <Money value={row.amount} /> : <></>,
                    },
                    {
                        key: 'unallocatedAmount',
                        name: <Trans i18nKey="BackOfficeUnallocatedAmount" />,
                        formatter: ({ row }) =>
                            row.type === 'MASTER' ? <Money value={row.unallocatedAmount} /> : <></>,
                    },
                    {
                        key: 'dt',
                        name: <Trans i18nKey="BackOfficePaymentDate" />,
                        formatter: ({ row }) =>
                            row.type === 'MASTER' ? <DataTimeText value={row.dt} /> : <></>,
                    },
                    { key: 'referenceId', name: <Trans i18nKey="BackOfficeExternalCode" /> },
                    { key: 'orderId', name: <Trans i18nKey="BackOfficeAppId" /> },
                ]}
            >
                <DataGrid<ClientPaymentInfoTable>
                    reactDataGreedProps={{
                        defaultColumnOptions: {
                            sortable: true,
                            resizable: true,
                        },
                        onRowsChange: onRowsChange,
                        rowHeight: ({ type, row }) =>
                            type === 'ROW' && row.type === 'DETAIL'
                                ? 85 + row.covers.length * 45
                                : 45,
                    }}
                    rows={tableInfo ?? []}
                    blockSize={blockSize}
                    minBlockSize={90}
                    loading={paymentsQuery.isFetching}
                />
            </GridColumnsConfigProvider>
        </>
    );
};

export default PaymentsTab;
